export const flatten = (nodes, out = []) => {
  for (const n of nodes ?? []) {
    out.push(n);
    if (n.children?.length) flatten(n.children, out);
  }
  return out;
};

const uniqueCountries = (tree) => {
  const seen = new Map();
  for (const n of flatten(tree)) {
    if (!n.country) continue;
    const key = String(n.country);
    if (!seen.has(key)) {
      seen.set(key, n.countryLabel || key);
    }
  }
  return [...seen.entries()]
    .map(([key, label]) => ({ key, label }))
    .sort((a, b) => String(a.label).localeCompare(String(b.label)));
};

const flatNodes = (nodes) => nodes.map((n) => ({ ...n, children: [] }));

const collectHierarchyEntries = (tree = []) => {
  const collected = [];

  const visit = (nodes, ancestors = []) => {
    for (const node of nodes ?? []) {
      if (!node) continue;

      const isGroup = Boolean(node.isGroup);
      const nextAncestors =
        !isGroup && node.id
          ? [
              ...ancestors,
              {
                id: String(node.id),
                name: node.name,
                unitLevel: node.unitLevel,
              },
            ]
          : ancestors;

      if (!isGroup && node.id) {
        collected.push({
          ...node,
          parentHierarchyLabel: ancestors
            .map((item) => item.name)
            .filter(Boolean)
            .join(" > "),
          fullHierarchyLabel: nextAncestors
            .map((item) => item.name)
            .filter(Boolean)
            .join(" > "),
        });
      }

      visit(node.children ?? [], nextAncestors);
    }
  };

  visit(tree);
  return collected;
};

const countryEntries = (tree = []) =>
  uniqueCountries(tree).map(({ key, label }) => ({
    id: `country:${key}`,
    name: label,
    unitLevel: "country",
    country: key,
    countryLabel: label,
    children: [],
    parentHierarchyLabel: "",
    fullHierarchyLabel: label,
  }));

export const buildPickerOptions = (tree, hierarchy) => {
  switch (hierarchy) {
    case "company":
      return flatNodes(flatten(tree).filter((n) => n.unitLevel === "level_2"));
    case "businessUnit":
      return flatNodes(flatten(tree).filter((n) => n.unitLevel === "level_1"));
    case "site":
      return flatNodes(flatten(tree).filter((n) => n.unitLevel === "level_3"));
    case "country":
      return uniqueCountries(tree).map(({ key, label }) => ({
        id: `country:${key}`,
        name: label,
        unitLevel: "country",
        country: key,
        countryLabel: label,
        children: [],
      }));
    case "all":
    default:
      return tree;
  }
};

export const listOrganizationsForHierarchy = (tree, hierarchy) => {
  const hierarchyEntries = collectHierarchyEntries(tree);

  switch (hierarchy) {
    case "company":
      return hierarchyEntries.filter((node) => node.unitLevel === "level_2");
    case "businessUnit":
      return hierarchyEntries.filter((node) => node.unitLevel === "level_1");
    case "site":
      return hierarchyEntries.filter((node) => node.unitLevel === "level_3");
    case "country":
      return countryEntries(tree);
    case "all":
    default:
      return hierarchyEntries;
  }
};

export const buildSelectedOrganizationEntries = (
  tree,
  hierarchy,
  selectedIds,
) => {
  const items = listOrganizationsForHierarchy(tree, hierarchy);
  if (!selectedIds?.length) return items;

  const selectedIdSet = new Set(selectedIds.map(String));
  return items.filter((item) => selectedIdSet.has(String(item.id)));
};

export const subtreeIds = (tree, id) => {
  const out = [];
  const visit = (nodes) => {
    for (const n of nodes ?? []) {
      if (String(n.id) === id) {
        out.push(String(n.id));
        for (const d of flatten(n.children ?? [])) out.push(String(d.id));
        return true;
      }
      if (visit(n.children)) return true;
    }
    return false;
  };
  visit(tree);
  return out;
};

export const resolveOrgIds = (orgIds, tree) => {
  if (!orgIds?.length) return [];

  const out = new Set();
  for (const id of orgIds) {
    if (typeof id === "string" && id.startsWith("country:")) {
      const country = id.slice("country:".length);
      for (const n of flatten(tree)) {
        if (n.country === country) out.add(String(n.id));
      }
    } else {
      for (const sub of subtreeIds(tree, String(id))) out.add(sub);
      // const resolvedSubtreeIds = subtreeIds(tree, String(id));
      // if (resolvedSubtreeIds.length === 0) {
      //   out.add(String(id));
      // } else {
      //   for (const sub of resolvedSubtreeIds) out.add(sub);
      // }
    }
  }
  return [...out];
};

// Exact-pick resolution: synthetic country:* picker ids expand to their
// member org ids; real ids pass through as picked, with no subtree
// expansion. Used only by TargetSetupOverviewPage store seeding, whose
// exact-pick semantics are a separate documented product decision. The
// dashboard drill-through URLs carry the subtree-expanded selection
// instead (see expandCarriedOrgIds in data-entry-link.js).
export const resolveCarriedOrgIds = (orgIds, tree) => {
  if (!orgIds?.length) return [];

  const out = new Set();
  for (const id of orgIds) {
    if (typeof id === "string" && id.startsWith("country:")) {
      const country = id.slice("country:".length);
      for (const n of flatten(tree)) {
        if (n.country === country) out.add(String(n.id));
      }
    } else {
      out.add(String(id));
    }
  }
  return [...out];
};
